/**
 * Secure logging utility that replaces console.log statements
 * Provides structured logging while sanitizing sensitive data
 */

type LogLevel = 'info' | 'warn' | 'error' | 'debug';

interface LogEntry {
  timestamp: string;
  level: LogLevel;
  context: string;
  message: string;
  data?: Record<string, any>;
}

class SecureLogger {
  private isProduction = process.env.NODE_ENV === 'production';
  private sensitiveKeys = ['password', 'pin', 'token', 'key', 'secret', 'email', 'phone'];

  private sanitizeData(data: any): any {
    if (!data || typeof data !== 'object') return data;
    
    const sanitized = { ...data };
    
    for (const key in sanitized) {
      if (this.sensitiveKeys.some(sensitive => 
        key.toLowerCase().includes(sensitive.toLowerCase())
      )) {
        sanitized[key] = '[REDACTED]';
      } else if (typeof sanitized[key] === 'object') {
        sanitized[key] = this.sanitizeData(sanitized[key]);
      }
    }
    
    return sanitized;
  }

  private log(level: LogLevel, context: string, message: string, data?: Record<string, any>) {
    const entry: LogEntry = {
      timestamp: new Date().toISOString(),
      level,
      context,
      message,
      data: data ? this.sanitizeData(data) : undefined
    };

    // In development, use console for debugging
    if (!this.isProduction) {
      const logFn = level === 'error' ? console.error : 
                   level === 'warn' ? console.warn : console.log;
      
      logFn(`[${entry.level.toUpperCase()}] [${entry.context}] ${entry.message}`, 
            entry.data ? entry.data : '');
    }

    // TODO: In production, send to secure logging service
    // This could be integrated with Supabase functions or external services
  }

  info(context: string, message: string, data?: Record<string, any>) {
    this.log('info', context, message, data);
  }

  warn(context: string, message: string, data?: Record<string, any>) {
    this.log('warn', context, message, data);
  }

  error(context: string, message: string, data?: Record<string, any>) {
    this.log('error', context, message, data);
  }

  debug(context: string, message: string, data?: Record<string, any>) {
    this.log('debug', context, message, data);
  }
}

export const secureLogger = new SecureLogger();